import { Button, Divider, Spin } from "antd";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import infoImage from "@images/info-icon.svg";
import "@styles/styles-Calendar.css";
import Popout from "@components/Common/Popout";
import { DateCellContentV2 } from "./DateCellContentV2";

/**
 * DateInfoPopout's counterpart for the /calendar/day contract. Same layout as v1,
 * but metrics arrive denormalised (one row per metric, colour and decimals
 * included) instead of as inline info[key] + info.colors lookups, drills carry
 * ids, and the cell colour is a resolved hex.
 *
 * The popout is mounted as soon as a day is clicked and shows a spinner until the
 * day payload lands, so the click feels immediate on a slow request.
 */

interface ICalendarDayMetric {
	key: string,
	name: string,
	value?: number | null,
	color?: string | null,
	decimal_places?: number | null,
	visible?: boolean | null,
	is_rpe?: boolean
}

interface ICalendarDayDrill {
	id: number,
	name?: string | null,
	duration?: number | null
}

interface ICalendarDay {
	date: string,
	color?: string,
	fill?: number | null,
	score?: number | null,
	is_game?: boolean,
	opponent?: string | null,
	home?: boolean | null,
	is_prediction?: boolean,
	has_training?: boolean,
	has_injuries?: boolean,
	wellness_score?: number | null,
	injuries?: string[],
	lift?: string | null,
	drills?: ICalendarDayDrill[],
	metrics?: ICalendarDayMetric[]
}

type DayTargetMethod = "practice_plan" | "fixed_values" | null;

// Day targets. The overview calendar passes these props; without a method the popout is the day detail.
interface IDayTargetProps {
	dayTargetMethod?: DayTargetMethod,
	onDeleteDayTarget?: () => void
}

interface IDateInfoPopoutV2 extends IDayTargetProps {
	info: ICalendarDay | null,
	loading: boolean,
	showInfoPopout: boolean,
	setShowInfoPopout: React.Dispatch<boolean>,
	openPredictionDialog: (date: string) => void,
	handleDeletePracticePlan: () => void,
	showHelp: () => void
}

const formatNumber = (num: number, decimalPlaces: number) => {
	try {
		return Number(num)?.toFixed(decimalPlaces);
	} catch (e) {
		return '';
	}
};

const Metrics = ({ metrics }: { metrics: ICalendarDayMetric[] }) => (
	<>
		{metrics
			.filter((metric) => metric?.key && !metric.is_rpe)
			.filter((metric) => metric.visible !== false)
			.map((metric, index) => (
				<p
					key={index}
					style={{
						fontWeight: "bold",
						color: metric.color || '#cccccc',
						margin: "4px 0",
						textAlign: 'left',
						textIndent: '-30px',
						marginLeft: '30px'
					}}
				>{`${metric.name}: ${formatNumber(metric.value || 0, metric.decimal_places ?? 2)}`}</p>
			))}
	</>
);

const Drills = ({ drills, dateString }: { drills: ICalendarDayDrill[], dateString: string }) => (
	<div style={{ textAlign: "left" }}>
		<ol style={{ padding: "0 0 10px 0" }}>
			{drills.map((drill, i) => (
				<div key={i} style={{ textAlign: "left" }}>
					<li style={{ listStylePosition: "inside" }}>
						{drill.duration != null ? (
							`${drill.name} (${drill.duration} minutes)`
						) : (
							<Link to={`/drills?drill=${encodeURIComponent(drill.name?.trim())}&date=${dateString}`}>
								{drill.name}
							</Link>
						)}
					</li>
				</div>
			))}
		</ol>
	</div>
);

const Footer = ({
	showPredictionButtons,
	dateString,
	handleDeletePracticePlan,
	openPredictionDialog,
	dayTargetMethod,
	onDeleteDayTarget
}: {
	showPredictionButtons: boolean,
	dateString: string,
	handleDeletePracticePlan: IDateInfoPopoutV2['handleDeletePracticePlan'],
	openPredictionDialog: IDateInfoPopoutV2['openPredictionDialog']
} & IDayTargetProps) => {
	const navigate = useNavigate();

	// A day target of either method: one "Delete" (it asks first) and one "Edit".
	if (dayTargetMethod) {
		return (
			<>
				<Button danger type="primary" className="delete-button" onClick={onDeleteDayTarget}>
					<div>Delete</div>
				</Button>
				<Button className="calendar-nav-link" onClick={() => openPredictionDialog(dateString)}>
					Edit
				</Button>
			</>
		);
	}

	return (
		<>
			{showPredictionButtons && (
				<Button
					danger
					type="primary"
					className="delete-button"
					onClick={handleDeletePracticePlan}
				>
					<div>
						Delete predictions
					</div>
				</Button>
			)}

			<Button
				className="calendar-nav-link"
				onClick={() =>
					showPredictionButtons
						? openPredictionDialog(dateString)
						: navigate(`/breakdown/${dateString}`)
				}
			>
				{showPredictionButtons ? "Edit Plan" : "Go To Breakdown"}
			</Button>
		</>
	);
};

const DayDetail = ({
	info,
	openPredictionDialog,
	handleDeletePracticePlan,
	showHelp,
	dayTargetMethod,
	onDeleteDayTarget
}: {
	info: ICalendarDay,
	openPredictionDialog: IDateInfoPopoutV2['openPredictionDialog'],
	handleDeletePracticePlan: IDateInfoPopoutV2['handleDeletePracticePlan'],
	showHelp: IDateInfoPopoutV2['showHelp']
} & IDayTargetProps) => {
	const {
		date: dateString,
		opponent,
		home,
		score,
		drills,
		injuries,
		lift,
		is_prediction: isPrediction,
		has_training: hasTraining,
		wellness_score: wellnessScore,
		metrics = []
	} = info;

	const showPredictionButtons = Boolean(isPrediction && !hasTraining);
	const hasDrills = Boolean(drills && drills.length !== 0);
	// A fixed-values day has no drills; it keeps the practice plan day layout, so both line up.
	const planLayout = hasDrills || dayTargetMethod === "fixed_values";

	// Determine wellness score presentation
	let wellnessScoreClassName = "";
	if (wellnessScore != null) {
		if (wellnessScore >= 75) {
			wellnessScoreClassName = "text-success";
		} else if (wellnessScore >= 50) {
			wellnessScoreClassName = "text-warning";
		} else {
			wellnessScoreClassName = "text-danger";
		}
	}

	// A game with nothing recorded against it: /v3/year seeds those dates with only
	// { color: 'blue', opponent, home }, so the opponent is all there is to show.
	// v1 detects this by the colour *name* being 'blue'; the day endpoint resolves
	// that to a hex it also uses as its unknown-colour fallback, so matching the hex
	// would be both fragile and able to hide a real session. Asking whether any
	// detail actually came back is equivalent for the seeded rows and can never
	// swallow data.
	const hasAnyDetail = Boolean(
		hasTraining ||
		score != null ||
		wellnessScore != null ||
		injuries?.length ||
		lift != null ||
		drills?.length ||
		metrics.some((metric) => metric.value != null)
	);

	if (opponent && !hasAnyDetail) {
		return (
			<div>
				<p>
					Opponent: {!home ? "@" : "vs."} {opponent}
				</p>
			</div>
		);
	}

	return (
		<>
			<div style={{ display: 'flex', justifyContent: 'space-between' }}>
				<DateCellContentV2
					info={info}
					date={dateString}
					showGameAndTraining={true}
					fontSize={20}
					popOutView={true}
				/>
				<div style={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
					<p style={{ fontWeight: "bold", fontSize: 18, marginBottom: '0rem' }}>{dateString}</p>
					<img
						src={infoImage}
						alt="info-img"
						style={{ cursor: "pointer", width: "20px", height: "20px" }}
						onClick={() => showHelp()}
					/>
				</div>
			</div>

			<Divider style={{ margin: "12px 0" }} />

			{dayTargetMethod && (
				<p className="day-target-title">
					Day target ({dayTargetMethod === "fixed_values" ? "fixed values" : "practice plan"})
				</p>
			)}

			<div style={{ flex: 1, overflow: "auto" }}>
				<div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', columnGap: '30px', borderBottom: '1px solid lightgrey', paddingBottom: '20px' }}>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "flex-start",
							overflowY: 'auto',
							maxHeight: '280px',
							width: planLayout ? '50%' : '80%',
							borderRight: hasDrills ? '1px solid lightgrey' : undefined,
						}}
					>
						<Metrics metrics={metrics} />
					</div>

					{hasDrills && (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-start",
								overflowY: 'auto',
								maxHeight: '280px',
								width: '40%',
							}}
						>
							<Drills dateString={dateString} drills={drills as ICalendarDayDrill[]} />
						</div>
					)}
					{planLayout && !hasDrills && <div className="day-target-drills-space" />}
				</div>

				{wellnessScore != null && (
					<div style={{ textAlign: "left" }}>
						<p className={wellnessScoreClassName}>
							Wellness Score: {wellnessScore}
						</p>
					</div>
				)}

				{opponent && (
					<div style={{ textAlign: "left" }}>
						<p className="html-metrics">Opponent: {opponent}</p>
					</div>
				)}

				<div style={{ textAlign: "left", marginBottom: 8 }}>
					{injuries?.length ? (
						<>
							<p>Injuries: </p>
							<ul>
								{injuries.map((injury, i) => (
									<li key={i}>{injury}</li>
								))}
							</ul>
						</>
					) : null}
				</div>

				{lift != null && <div style={{ textAlign: "left" }}><p>Lift: {lift}</p></div>}
			</div>

			<div className="footer-button-container" style={{ marginTop: "12px" }}>
				<Footer
					showPredictionButtons={showPredictionButtons}
					dateString={dateString}
					handleDeletePracticePlan={handleDeletePracticePlan}
					openPredictionDialog={openPredictionDialog}
					dayTargetMethod={dayTargetMethod}
					onDeleteDayTarget={onDeleteDayTarget} />
			</div>
		</>
	);
};

export const DateInfoPopoutV2 = ({
	info,
	loading,
	openPredictionDialog,
	showInfoPopout,
	setShowInfoPopout,
	handleDeletePracticePlan,
	showHelp,
	dayTargetMethod,
	onDeleteDayTarget
}: IDateInfoPopoutV2) => (
	<Popout
		showPopout={showInfoPopout}
		setShowPopout={setShowInfoPopout}
		popOutStyles={{
			maxHeight: "900px",
			width: "100%",
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
		}}
		cardStyles={{
			overflow: "scroll",
			width: "90%",
			maxHeight: "800px",
			display: "flex",
			flexDirection: "column"
		}}
	>
		<div style={{
			display: "flex",
			flexDirection: "column",
			height: "100%",
			maxHeight: "inherit",
			overflow: "hidden"
		}}>
			{loading ? (
				<div className="calendar-day-popout-placeholder">
					<Spin size="large" />
				</div>
			) : !info ? (
				// Settled with nothing to show — the request failed and already
				// surfaced a message, so keep the popout from spinning forever.
				<div className="calendar-day-popout-placeholder">
					<p>No details available for this day.</p>
				</div>
			) : (
				<DayDetail
					info={info}
					openPredictionDialog={openPredictionDialog}
					handleDeletePracticePlan={handleDeletePracticePlan}
					showHelp={showHelp}
					dayTargetMethod={dayTargetMethod}
					onDeleteDayTarget={onDeleteDayTarget}
				/>
			)}
		</div>
	</Popout>
);

export default DateInfoPopoutV2;
